import { configureStore, combineReducers } from '@reduxjs/toolkit';
import createSagaMiddleware from 'redux-saga';
import { persistStore, persistReducer, createTransform } from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import rootSaga from './rootSaga.js';
import { authReducer } from '../features/auth/store/authSlice.js';
import { coursesListReducer } from '../features/courses/store/coursesListSlice.js';
import { courseDetailReducer } from '../features/courses/store/courseDetailSlice.js';
import { lessonReducer } from '../features/lesson/store/lessonSlice.js';
import { coachingReducer } from '../features/coaching/store/coachingSlice.js';
import { libraryReducer } from '../features/library/store/librarySlice.js';
import { accountReducer } from '../features/account/store/accountSlice.js';
import { notificationsReducer } from '../features/notifications/store/notificationsSlice.js';
import { toastReducer } from '../features/system/store/toastSlice.js';
import { proctorReducer } from '../features/proctoring/store/proctorSlice.js';
import { progressReducer } from '../features/progress/store/progressSlice.js';
import { featureVisibilityReducer } from '../features/appVisibility/store/featureVisibilitySlice.js';
import { signedOut, signInSucceeded, sessionExpired } from '../features/auth/store/authSlice.js';
import { configureAuth } from '../lib/api.js';
import { selectAuthToken } from '../features/auth/store/selectors.js';
import { resolveOnboardingPending } from '../features/auth/lib/onboardingStatus.js';

const appReducer = combineReducers({
  auth: authReducer,
  coursesList: coursesListReducer,
  courseDetail: courseDetailReducer,
  lesson: lessonReducer,
  coaching: coachingReducer,
  library: libraryReducer,
  account: accountReducer,
  notifications: notificationsReducer,
  system: toastReducer,
  proctor: proctorReducer,
  progress: progressReducer,
  featureVisibility: featureVisibilityReducer,
});

export function rootReducer(state, action) {
  if (action.type === signedOut.type) {
    return appReducer(undefined, action);
  }
  if (action.type === signInSucceeded.type) {
    return appReducer({ auth: state?.auth }, action);
  }
  return appReducer(state, action);
}

// Only persist the session identity fields on the auth slice. `status` and
// `error` are transient UI state — persisting them rehydrates stale errors
// (e.g. a "Network Error" from a previous attempt) on the next page load.
const authPersistTransform = createTransform(
  (inbound) => ({
    token: inbound.token,
    user: inbound.user,
    org: inbound.org,
    onboardingPending: inbound.onboardingPending,
    loginSource: inbound.loginSource,
    ssoContext: inbound.ssoContext,
  }),
  (outbound) => ({
    status: outbound.token || outbound.user ? 'authenticated' : 'anonymous',
    token: outbound.token ?? null,
    user: outbound.user ?? null,
    org: outbound.org ?? null,
    loginSource: outbound.loginSource ?? null,
    ssoContext: outbound.ssoContext ?? null,
    error: null,
    // Plan E additions — ephemeral flow state never persists; always rehydrate at idle.
    otpStatus: 'idle',
    otpError: null,
    forgotStatus: 'idle',
    forgotError: null,
    signupContext: null,
    ssoStatus: 'idle',
    ssoError: null,
    onboardingPending: resolveOnboardingPending({
      onboardingPending: outbound.onboardingPending,
      user: outbound.user,
    }),
  }),
  { whitelist: ['auth'] },
);

const persistConfig = {
  key: 'step-web-v1',
  storage,
  whitelist: ['auth'],
  transforms: [authPersistTransform],
};

const sagaMiddleware = createSagaMiddleware();

export const store = configureStore({
  reducer: persistReducer(persistConfig, rootReducer),
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      thunk: false,
      serializableCheck: {
        ignoredActions: [
          'persist/PERSIST',
          'persist/REHYDRATE',
          'persist/PAUSE',
          'persist/PURGE',
          'persist/REGISTER',
          'persist/FLUSH',
          'coaching/assignmentSubmitting',
        ],
      },
    }).concat(sagaMiddleware),
  devTools: import.meta.env.VITE_ENV !== 'production',
});

sagaMiddleware.run(rootSaga);

export const persistor = persistStore(store);

configureAuth({
  getToken: () => selectAuthToken(store.getState()),
  onUnauthorized: () => store.dispatch(sessionExpired()),
});
